import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { SiteSection } from '@serasa/shared/enums/site-section.enum';
import { AnalyticsService } from '@serasa/shared/services/analytics.service';
import { SidenavManagerService } from '@serasa/shared/services/sidenav.manager.service';
import { Utils } from '@serasa/shared/utils/utils';
import { Observable, ReplaySubject, takeUntil } from 'rxjs';
import {
  AlphanumericRegexValidator,
  EmailDomainValidator,
  MinMaxLengthRegexValidator,
} from '../../../../../shared/validators';
import { CloneLogon } from '../../interfaces/clone-logon.interface';
import { CloneableLogonSearch } from '../../interfaces/cloneable-logon-search.interface';
import {
  addClonedUser,
  cleanClonedUser,
  closeSideNav,
  loadCloneableLogonSearchData,
  loadSelectedLogon,
} from '../../state/actions/user.actions';
import {
  selectCloneableLogonSearch,
  selectClonedLogon,
} from '../../state/selectors/user.selectors';
import { UserAddMessagesHelper } from './helpers/user-add-messages.helper';
import {
  IUserAddSearchMessagesHelper,
  UserAddSearchMessagesHelper,
} from './helpers/user-add-search-messages.helper';

@Component({
  selector: 'pl-user-add',
  templateUrl: './user-add.component.html',
  styleUrls: ['./user-add.component.scss'],
  standalone: false,
})
export class UserAddComponent implements OnInit, OnDestroy {
  public cloneableLogon$: Observable<CloneableLogonSearch | undefined>;
  public clonedLogon$: Observable<CloneLogon | undefined>;
  public searchCloneableLogonForm: FormGroup;
  public createClonedLogonForm: FormGroup;
  public errorMessage: IUserAddSearchMessagesHelper | undefined;
  public searchLoading = false;
  public createLogonLoading = false;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  public pageName?: string;

  constructor(
    private readonly store: Store,
    private readonly _fb: UntypedFormBuilder,
    private readonly _sidenavManagerService: SidenavManagerService,
    private readonly _analyticsService: AnalyticsService,
  ) {
    this.cloneableLogon$ = this.store.select(selectCloneableLogonSearch);
    this.clonedLogon$ = this.store.select(selectClonedLogon);

    this.searchCloneableLogonForm = this._fb.group({
      logon: [
        '',
        [
          Validators.required,
          new MinMaxLengthRegexValidator(
            7,
            9,
            'minMaxError',
            'O logon do usuário deve possuir entre 7 e 9 caracteres',
          ).validate(),
        ],
      ],
    });

    this.createClonedLogonForm = this._fb.group({
      name: ['', Validators.required],
      email: [
        '',
        [
          Validators.required,
          new EmailDomainValidator(Utils.emailRegex()).validate(),
        ],
      ],
      nameLogon: [
        '',
        [
          Validators.required,
          new AlphanumericRegexValidator(
            'alphanumericError',
            'A identificação do logon não deve possuir caracteres especiais (# $ ! -) ou acentos.',
          ).validate(),
          new MinMaxLengthRegexValidator(
            8,
            20,
            'minMaxError',
            'A identificação do logon deve possuir entre 8 e 20 caracteres.',
          ).validate(),
        ],
      ],
    });
  }

  public closeSideNav(): void {
    this.store.dispatch(closeSideNav());
  }

  public ngOnInit() {
    this.pageName = `${SiteSection.CLONAR_USUARIO}:Busca`;
    this._analyticsService.doPageLoad(this.pageName);
    this.cloneableLogon$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((logonSearchData) => {
        this.searchLoading = false;

        if (logonSearchData?.logon) {
          this.pageName = `${SiteSection.CLONAR_USUARIO}:Criar`;
          this._analyticsService.doPageLoad(this.pageName);
        }
        if (logonSearchData?.error) {
          this.errorMessage = new UserAddSearchMessagesHelper(
            logonSearchData.error,
          ).error;
        }
      });

    this.clonedLogon$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((logonData) => {
        this.createLogonLoading = false;
        if (logonData?.logon) {
          this.store.dispatch(
            loadSelectedLogon({
              selectedUserId: logonData.logon.userId!,
              userDataTable: {
                logon: logonData.logon.logon,
              },
            }),
          );
          this._sidenavManagerService.navigate$.next('add-confirmation');
        }

        if (logonData?.error) {
          this.errorMessage = new UserAddMessagesHelper(logonData.error).error;
        }
      });
  }

  public searchCloneableLogonData() {
    this.searchLoading = true;
    this.store.dispatch(
      loadCloneableLogonSearchData({ ...this.searchCloneableLogonForm.value }),
    );
  }

  public cleanCloneableLogonData() {
    this.searchCloneableLogonForm.reset();
    this.createClonedLogonForm.reset();
    this.store.dispatch(cleanClonedUser());
  }

  public addUser() {
    this.createLogonLoading = true;
    this.store.dispatch(
      addClonedUser({
        payload: {
          ...this.createClonedLogonForm.value,
          ...this.searchCloneableLogonForm.value,
        },
      }),
    );
  }

  public ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.cleanCloneableLogonData();
  }
}
